import React, { useCallback, useEffect, useRef, useState } from 'react';
import DiscoveryHeader from './DiscoveryHeader';
import DiscoveryFilter from './DiscoveryFilter';
import {
    DiscoveryResponse,
    defaultAudience,
    defaultInfluencer,
    IDiscoveryFilterPlatform,
    initialFilterState,
    IProfileIds,
    IUiDefaultPlatforms,
    MoodashActivePlatforms,
} from '../dto/IDiscoveryFilter';
import DiscoveryInfluencerCardItem from './DiscoveryInfluencerCardItem';
import { DiscoveryService } from '../service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import DiscoveryInfluencerList from './DiscoveryInfluencerList';
import useMoodashData from '../../../../../shared/hooks/useMoodashData';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { Pagination } from '../../../../../shared/components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { useNavigate } from 'react-router-dom';
import { ExternalSearchDetailModal } from '../../book/external-search/components/modal';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import DiscoveryTotalsSection from './DiscoveryTotalSection';
import { actions } from '../../../../../store/authentication/authRedux';
import { NO_PROFILE_TOKENS_KEY, NO_SEARCH_TOKENS_KEY } from '../../../../../utils/constants';
import { openRefillModal } from '../../../../../store/subscription/subscriptionSlice';
import { RefillTokenModal } from '../../../../../shared/components/stripe/RefillTokenModal';

const Discovery = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { activeCampaignId, isAclLoaded } = useSelector((state: IAllStates) => state.selectedCampaign);

    const [response, setResponse] = useState<DiscoveryResponse>({
        lookalikes: [],
        total: 0,
        info: { isClient: false },
    });
    const [page, setPage] = useState(1);
    const {
        interests,
        locations,
        audienceLocations,
        languages,
        topics,
        hashtags,
        users,
        mentions,
        filterParams: filterParamsMoodash,
        setFilterParams,
        presetOptions,
        setPresetOptionFilter,
        presetOptionFilter,
    } = useMoodashData();
    const [hasSearched, setHasSearched] = useState(false);
    const [favoritesCount, setFavoritesCount] = useState(0);
    const [showExternalProfile, setShowExternalProfile] = useState(false);
    const [activePlatform, setActivePlatform] = useState(MoodashActivePlatforms.INSTAGRAM);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showConfirmAddModal, setShowConfirmAddModal] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState<IProfileIds>({
        ivId: undefined,
        externalId: undefined,
        ivProfileId: undefined,
        offerId: undefined,
    });
    const [filters, setFilters] = useState<IDiscoveryFilterPlatform>({
        instagram: { influencer: {}, audience: {} },
        tiktok: { influencer: {}, audience: {} },
        youtube: { influencer: {}, audience: {} },
    });
    const [uiDefaultState, setUiDefaultState] = useState<IUiDefaultPlatforms>({
        instagram: { influencer: {}, audience: {} },
        tiktok: { influencer: {}, audience: {} },
        youtube: { influencer: {}, audience: {} },
    });
    // const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [loadUi, setLoadUi] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const [selectedPresetOption, setSelectedPresetOption] = useState<number | null>(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        function getNbFavorites() {
            DiscoveryService.getNbFavorites(activeCampaignId)
                .then(data => {
                    setFavoritesCount(data?.favoritesCount);
                    setUiDefaultState(() => ({
                        instagram: {
                            influencer: {
                                ...defaultInfluencer,
                                ...data?.uiDefaults?.instagram?.influencer,
                            },
                            audience: {
                                ...defaultAudience,
                                ...data?.uiDefaults?.instagram?.audience,
                            },
                        },
                        youtube: {
                            influencer: {
                                ...defaultInfluencer,
                                ...data?.uiDefaults?.youtube?.influencer,
                            },
                            audience: {
                                ...defaultAudience,
                                ...data?.uiDefaults?.youtube?.audience,
                            },
                        },
                        tiktok: {
                            influencer: {
                                ...defaultInfluencer,
                                ...data?.uiDefaults?.tiktok?.influencer,
                            },
                            audience: {
                                ...defaultAudience,
                                ...data?.uiDefaults?.tiktok?.audience,
                            },
                        },
                    }));
                    setFilters(data?.filters);
                })
                .catch(error => ErrorToast(error))
                .finally(() => setLoadUi(true));
        }

        getNbFavorites();
        return () => setLoadUi(false);
    }, []);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        DiscoveryService.saveCurrentFilter({
            campaignId: activeCampaignId ?? 0,
            platformName: filterParamsMoodash?.platform?.toLowerCase(),
            filter: filters[filterParamsMoodash?.platform?.toLowerCase()],
            uiDefaults: uiDefaultState[filterParamsMoodash?.platform?.toLowerCase()],
        })
            .then(() => {})
            .catch(error => ErrorToast(error));
    }, [filters]);

    const loadDiscovery = useCallback(
        (customPage?: number) => {
            const currentPage = customPage ?? page;
            const queryParams = new URLSearchParams({
                page: (currentPage - 1).toString(),
                platform: filterParamsMoodash?.platform?.toLowerCase(),
                campaignId: activeCampaignId ? activeCampaignId.toString() : '',
            }).toString();
            DiscoveryService.getDiscovery(filters[filterParamsMoodash?.platform.toLowerCase()], queryParams)
                .then(data => {
                    setResponse(data);
                    setHasSearched(true);
                    setActivePlatform(filterParamsMoodash?.platform);
                })
                .catch(err => {
                    if (err?.response?.data?.message === NO_SEARCH_TOKENS_KEY) {
                        setShowConfirmAddModal(false);
                        dispatch(
                            openRefillModal({
                                source: NO_SEARCH_TOKENS_KEY,
                            })
                        );
                    } else {
                        ErrorToast(err);
                    }
                });
        },
        [page, filters, filterParamsMoodash]
    );

    const fetchDiscovery = () => {
        if (page !== 1) {
            setPage(1);
        } else {
            loadDiscovery(1);
        }
    };

    const addToFavorites = (ivId?: number, externalId?: number, isFavorite?: boolean) => {
        if (response?.info?.isClient && isFavorite) {
            return;
        }

        DiscoveryService.addFavoriteUser({ campaignId: activeCampaignId ?? 0, ivId, externalId })
            .then(data => {
                SuccessToast(
                    t(
                        !isFavorite
                            ? 'influencer.explore.addToFavoriteSuccess'
                            : 'influencer.explore.removeFromFavoriteSuccess'
                    )
                );
                isFavorite
                    ? setFavoritesCount(prevState => prevState - 1)
                    : setFavoritesCount(prevState => prevState + 1);
                dispatch(actions.updateDiscoveryDetails(data?.discoveryDetails));
                setResponse(prev => ({
                    ...prev,
                    lookalikes: prev.lookalikes.map(item =>
                        item.profile?.ivId === ivId && item.profile?.externalId === externalId
                            ? { ...item, alreadyViewed: true, profile: { ...item.profile, isFavorite: !isFavorite } }
                            : item
                    ),
                }));
            })
            .catch((error: any) => {
                if (error?.response?.data?.message === NO_PROFILE_TOKENS_KEY) {
                    dispatch(openRefillModal({ source: NO_PROFILE_TOKENS_KEY }));
                } else {
                    ErrorToast(error);
                }
            });
    };
    const handlePagination = (updatePage: number) => {
        setPage(updatePage);
        loadDiscovery(updatePage);
    };

    const goToFavorites = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_DISCOVERY}/${activeCampaignId}/favorites`);
    };

    const removeFavoriteModalAction = (ivId: number, externalId: number, isFavorite: boolean) => {
        setSelectedProfile({
            ivId,
            externalId,
            isFavorite,
        });
        if (isFavorite && !response?.info?.isClient) {
            setShowConfirmModal(true);
        } else {
            setShowConfirmAddModal(true);
        }
    };

    const openModal = (
        externalId: number,
        isClient: boolean,
        ivId: number,
        ivProfileId: number,
        hasOffer?: boolean,
        offerId?: number,
        alreadyViewed?: boolean
    ) => {
        setShowExternalProfile(true);
        setSelectedProfile(prevState => ({
            ...prevState,
            ivId,
            externalId,
            ivProfileId,
            offerId,
        }));
    };
    const closeExternalModal = () => {
        setShowExternalProfile(false);
    };

    const updateAlreadyViewedAye = useCallback((ivId?: number, externalId?: number) => {
        setResponse(prev => ({
            ...prev,
            lookalikes: prev.lookalikes.map(item =>
                item.profile?.ivId === ivId && item.profile?.externalId === externalId
                    ? { ...item, alreadyViewed: true }
                    : item
            ),
        }));
    }, []);

    const presetFilterAction = (filterId: number) => {
        // setLoadUi(false);
        if (presetOptions) {
            const selectedPresetOption = presetOptions?.data?.find(option => option.id === filterId);
            const selectedPlatformName = selectedPresetOption?.platform.platformEnum;
            setFilters(prevState => ({
                ...prevState,
                ...(selectedPlatformName ? { [selectedPlatformName]: selectedPresetOption?.filterData } : {}),
            }));
            setUiDefaultState(prevState => ({
                ...prevState,
                ...(selectedPlatformName
                    ? {
                          [selectedPlatformName]: {
                              ...prevState[selectedPlatformName],
                              influencer: {
                                  ...defaultInfluencer,
                                  ...selectedPresetOption?.uiDefaults?.influencer,
                              },
                              audience: {
                                  ...defaultAudience,
                                  ...selectedPresetOption?.uiDefaults?.audience,
                              },
                          },
                      }
                    : {}),
            }));
            // setLoadUi(true);
        }
    };

    const clearFilterAction = () => {
        setFilters(prevState => ({
            ...prevState,
            [filterParamsMoodash.platform.toLowerCase()]:
                initialFilterState[filterParamsMoodash.platform.toLowerCase()],
        }));
        setUiDefaultState(prevState => ({
            ...prevState,
            [filterParamsMoodash.platform.toLowerCase()]:
                initialFilterState[filterParamsMoodash.platform.toLowerCase()],
        }));
        if (!!selectedPresetOption) {
            setSelectedPresetOption(null);
        }
        setResetKey(prev => prev + 1);
    };

    return isAclLoaded ? (
        <>
            <RefillTokenModal />
            {showExternalProfile && (
                <ExternalSearchDetailModal
                    id={selectedProfile?.ivId ?? selectedProfile?.externalId ?? 0}
                    isInternal={!!selectedProfile.ivId}
                    show={showExternalProfile}
                    closeModal={closeExternalModal}
                    updateAlreadyViewedAye={() =>
                        updateAlreadyViewedAye(selectedProfile.ivId, selectedProfile.externalId)
                    }
                />
            )}
            {showConfirmModal && selectedProfile && (
                <ConfirmationModal
                    title={t('brand.campaign.discovery.remove_from_favorites')}
                    description={t('brand.campaign.book.offer.modal.are_you_sure')}
                    show={showConfirmModal}
                    closeModal={() => setShowConfirmModal(false)}
                    action={() =>
                        addToFavorites(
                            selectedProfile?.ivId,
                            selectedProfile?.externalId,
                            selectedProfile.isFavorite ?? true
                        )
                    }
                />
            )}
            <ConfirmationModal
                title={t('brand.campaign.discovery.add_to_favorites?')}
                description={t('brand.campaign.discovery.add_to_favorites_desc')}
                show={showConfirmAddModal}
                closeModal={() => setShowConfirmAddModal(false)}
                action={() =>
                    addToFavorites(
                        selectedProfile?.ivId,
                        selectedProfile?.externalId,
                        selectedProfile.isFavorite ?? true
                    )
                }
            />
            <DiscoveryHeader
                presetOptions={presetOptions?.data!}
                presetFilterAction={presetFilterAction}
                presetFilterCount={+presetOptions?.count!}
                favoritesCount={favoritesCount}
                goToFavorites={goToFavorites}
                clearPresetFilter={clearFilterAction}
                presetFilterParams={presetOptionFilter}
                setPresetFilterParams={setPresetOptionFilter}
                setSelectedPresetOption={setSelectedPresetOption}
                selectedPresetOption={selectedPresetOption}
            />
            {loadUi && (
                <DiscoveryFilter
                    filters={filters}
                    setFilters={setFilters}
                    fetchDiscovery={fetchDiscovery}
                    setMoodashFilter={setFilterParams}
                    uiDefaults={uiDefaultState}
                    setUiDefaults={setUiDefaultState}
                    moodashFilters={filterParamsMoodash}
                    interests={interests}
                    locations={locations}
                    audienceLocations={audienceLocations}
                    hashtags={hashtags}
                    languages={languages}
                    topics={topics}
                    users={users}
                    mentions={mentions}
                    setResetKey={setResetKey}
                    resetKey={resetKey}
                    clearFilterAction={clearFilterAction}
                    setSelectedPresetOption={setSelectedPresetOption}
                    selectedPresetOption={selectedPresetOption}
                />
            )}
            <DiscoveryTotalsSection
                hasSearched={hasSearched}
                activePlatform={activePlatform}
                favoritesCount={favoritesCount}
                lookalikes={response.lookalikes}
                total={(response.directs?.length ?? 0) > 0 ? response.directs?.length : response?.total}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                goToFavorites={goToFavorites}
            />

            <DiscoveryInfluencerList
                selectedPlatform={filterParamsMoodash.platform}
                influencersFound={numberFormatter(
                    ((response.directs?.length ?? 0) > 0 ? response.directs?.length : response?.total) ?? 0
                )}
            >
                <>
                    {response?.directs && !!response?.directs.length && (
                        <>
                            {response?.directs?.map(user => (
                                <DiscoveryInfluencerCardItem
                                    user={user}
                                    key={user?.userId}
                                    selectedPlatform={filterParamsMoodash?.platform}
                                    addToFavorites={removeFavoriteModalAction}
                                    border={true}
                                    openModal={() =>
                                        openModal(
                                            user.profile.externalId,
                                            response.info.isClient,
                                            user.profile.ivId,
                                            user.profile.ivProfileId,
                                            user.hasOffer,
                                            user.offerId,
                                            user.alreadyViewed
                                        )
                                    }
                                    isClient={response.info.isClient}
                                />
                            ))}
                        </>
                    )}
                    {!!response.directs?.length && response.total > 0 && (
                        <p className="font-16 mt-3">
                            Total of{' '}
                            <span className="font-weight-bold font-16">{numberFormatter(response?.total ?? 0)}</span>{' '}
                            lookalikes found on{' '}
                            <span className="font-weight-bold font-16">{activePlatform?.toUpperCase()}</span>
                        </p>
                    )}
                    {response?.lookalikes && !!response?.lookalikes.length ? (
                        <>
                            {response?.lookalikes?.map(user => (
                                <DiscoveryInfluencerCardItem
                                    user={user}
                                    key={user?.userId}
                                    selectedPlatform={filterParamsMoodash?.platform}
                                    addToFavorites={removeFavoriteModalAction}
                                    openModal={() =>
                                        openModal(
                                            user.profile.externalId,
                                            response.info.isClient,
                                            user.profile.ivId,
                                            user.profile.ivProfileId,
                                            user.hasOffer,
                                            user.offerId,
                                            user.alreadyViewed
                                        )
                                    }
                                    isClient={response.info.isClient}
                                />
                            ))}
                        </>
                    ) : (
                        !response?.lookalikes.length &&
                        !response?.directs?.length && (
                            <>
                                {hasSearched ? (
                                    <EmptyState
                                        imgClass="w-80px mt-5"
                                        iconPath="/tempAssets/not-found.svg"
                                        title={t('brand.campaign.discovery.not_found_empty_title')}
                                        desc={t('brand.campaign.discovery.not_found_empty_desc')}
                                    />
                                ) : (
                                    <EmptyState
                                        imgClass="w-80px mt-5"
                                        iconPath="/icons/placeholders/img-empty-list-two.svg"
                                        title={t('brand.campaign.discovery.empty_state_search_title')}
                                        desc={t('brand.campaign.discovery.empty_state_search_desc')}
                                    />
                                )}
                            </>
                        )
                    )}
                </>
            </DiscoveryInfluencerList>
            {response?.total > 1 && (
                <Pagination
                    page={page}
                    totalPages={Math.ceil(response.total / 15) ?? 0}
                    handlePagination={handlePagination}
                />
            )}
        </>
    ) : null;
};

export default Discovery;
